import { z } from 'zod';
import { currencySchema, isoDateTime, slugSchema } from './common';

export const formationDomainSchema = z.enum([
  'implantologie',
  'orthodontie',
  'endodontie',
  'parodontologie',
  'chirurgie-orale',
  'pedodontie',
  'esthetique',
]);
export type FormationDomain = z.infer<typeof formationDomainSchema>;

export const formationLevelSchema = z.enum(['initiation', 'intermediaire', 'avance', 'tous-niveaux']);
export type FormationLevel = z.infer<typeof formationLevelSchema>;

export const formationStatusSchema = z.enum(['draft', 'published', 'archived']);
export type FormationStatus = z.infer<typeof formationStatusSchema>;

/**
 * Accent colour for the formation's cards / featured block on the public site.
 * Chosen by the admin to match the formation's poster (vert = green,
 * bleu = blue, rose = pink, rouge = red). Defaults to green.
 */
export const formationColorThemeSchema = z.enum(['vert', 'bleu', 'rose', 'rouge']);
export type FormationColorTheme = z.infer<typeof formationColorThemeSchema>;

/**
 * Registration status per session/édition.
 * - 'open'      → "S'inscrire" button visible
 * - 'soldout'   → "Complète" badge, button disabled
 * - 'custom'    → admin-typed text replaces the button (e.g. "Sur invitation")
 */
export const registrationStatusSchema = z.enum(['open', 'soldout', 'custom']);
export type RegistrationStatus = z.infer<typeof registrationStatusSchema>;

/** One date pair per programme module within an édition (1:1 with `programme[]`). */
export const moduleDateSchema = z.object({
  startDate: isoDateTime,
  endDate: isoDateTime,
});
export type ModuleDate = z.infer<typeof moduleDateSchema>;

/**
 * A session of a formation (UI label: "Édition"). One édition is one full
 * delivery of the formation in a given city, with its own dates **per module**.
 *
 * Legacy fields `startDate` / `endDate` / `registrationOpen` are kept for
 * back-compat reads — the public adapter derives display dates from
 * `moduleDates[0..n].startDate / endDate` and the new `registrationStatus`.
 */
export const sessionSchema = z.object({
  id: z.string(),
  /** Free-text label, e.g. "Édition juin 2026" or "Promotion 1". */
  name: z.string().default('Édition'),
  city: z.string().min(1),
  venue: z.string().default(''),
  seats: z.number().int().nonnegative(),
  /** Booked seats — recomputed by an admin trigger when inscriptions confirm; defaults to 0. */
  seatsBooked: z.number().int().nonnegative().default(0),
  /** One date pair per programme module. Length should match `programme.length`. */
  moduleDates: z.array(moduleDateSchema).default([]),
  registrationStatus: registrationStatusSchema.default('open'),
  /** Free text shown on the public site when `registrationStatus === 'custom'`. */
  customStatus: z.string().default(''),
  /**
   * Archived (past) édition — e.g. legacy cohorts imported from the historical
   * spreadsheet. NEVER shown on the public site (no inscription possible), but
   * visible to the admin so photos can be attributed to it and members can see
   * their session gallery in « Mon espace ».
   */
  archived: z.boolean().optional(),

  // ── Legacy fields (kept so old docs parse cleanly during the transition) ─
  /** @deprecated use `moduleDates` instead. */
  startDate: isoDateTime.optional(),
  /** @deprecated use `moduleDates` instead. */
  endDate: isoDateTime.optional(),
  /** @deprecated use `registrationStatus` instead. */
  registrationOpen: z.boolean().optional(),
});
export type Session = z.infer<typeof sessionSchema>;

/**
 * A module in the programme.
 *
 * Post-refactor a module carries ONLY pedagogical content (title, points,
 * formateurs). Dates moved to the édition (per-session `moduleDates`) and
 * the price moved to the formation-level `prixParModule`. Legacy fields
 * (`startDate`, `endDate`, `price`) are still accepted on read so existing
 * docs parse cleanly — they're ignored by the new admin form.
 */
export const programmeModuleSchema = z.object({
  /** 1-based ordering. "Module 1", "Module 2"… */
  order: z.number().int().positive(),
  title: z.string().min(1),
  bullets: z.array(z.string()).default([]),
  /** Optional: formateurs assigned to this specific module. Subset of formation.formateurIds. */
  formateurIds: z.array(z.string()).default([]),

  // ── Legacy (will be stripped by the migration) ──────────────────────────
  /** @deprecated module dates moved to édition.moduleDates. */
  startDate: isoDateTime.optional(),
  /** @deprecated module dates moved to édition.moduleDates. */
  endDate: isoDateTime.optional(),
  /** @deprecated module price replaced by formation-level prixParModule. */
  price: z.number().nonnegative().optional(),
});
export type ProgrammeModule = z.infer<typeof programmeModuleSchema>;

/** @deprecated kept temporarily for transition — use `programmeModuleSchema`. */
export const programmeDaySchema = programmeModuleSchema;
/** @deprecated kept temporarily for transition — use `ProgrammeModule`. */
export type ProgrammeDay = ProgrammeModule;

/**
 * Per-formation certificate template configuration. The admin uploads the
 * empty template PDF + the signer's signature PNG once, then sets where the
 * participant name and signature go on the page (visual drag editor in admin).
 *
 * Coordinates use PDF points (1pt = 1/72 inch), origin at the page's
 * bottom-left — that's pdf-lib's native coordinate system, so the generator
 * uses these values directly.
 */
/** Title prefix prepended to the participant's name on the certificate. */
export const namePrefixSchema = z.enum(['none', 'Dr.', 'Docteur']);
export type NamePrefix = z.infer<typeof namePrefixSchema>;

export const certificateTemplateSchema = z.object({
  /** Template PDF (uploaded via FileUpload to formations/{id}/certificate/...). */
  templateUrl: z.string().url().nullable().default(null),
  /** Transparent PNG of the signer's signature. */
  signatureUrl: z.string().url().nullable().default(null),
  /** Name placement on the template (PDF points; origin bottom-left). */
  name: z.object({
    x: z.number(),
    y: z.number(),
    fontSize: z.number().int().positive().default(28),
    /** Optional title prepended to the name ("Dr. Prénom NOM"). */
    prefix: namePrefixSchema.default('none'),
  }),
  /** Signature placement on the template (PDF points). Height auto-scales. */
  signature: z.object({
    x: z.number(),
    y: z.number(),
    width: z.number().positive().default(140),
  }),
  /** Optional date overlay (PDF points). Generator computes the text from the
   *  participant's session end date in the chosen format. */
  date: z
    .object({
      x: z.number(),
      y: z.number(),
      fontSize: z.number().int().positive().default(14),
      format: z.enum(['long', 'month-year', 'year']).default('long'),
    })
    .optional(),
  /** Optional year-only overlay rendered rotated 90° (counterclockwise,
   *  reading bottom-to-top) in white. Use case: vertical year label along a
   *  decorative band of the template. */
  dateYear: z
    .object({
      x: z.number(),
      y: z.number(),
      fontSize: z.number().int().positive().default(24),
    })
    .optional(),
  /**
   * Manual override for the year printed on the certificate — applies to BOTH
   * the `date` field (any format) AND the `dateYear` overlay. When set, the
   * year replaces the one derived from the participant's session (last module
   * end date). Leave empty to fall back to the automatic computation. Useful
   * when the certificate's stamped year should reflect the year of issue
   * rather than the effective session year.
   */
  manualYear: z.number().int().min(2000).max(2100).optional(),
});
export type CertificateTemplate = z.infer<typeof certificateTemplateSchema>;

/**
 * Admin-editable "Infos pratiques" overrides. Every field is optional — when
 * empty, the public detail page falls back to its sensible default text. The
 * admin only fills a field when they want to override it for this formation.
 */
export const practicalInfoSchema = z.object({
  /** Note under the auto-shown venue address (e.g. accueil time, café offert). */
  lieu: z.string().max(500).optional(),
  /** What's included (support, repas, certificat, replay…). */
  inclus: z.string().max(800).optional(),
  /** Pré-requis. When empty, defaults to a level-derived sentence. */
  prerequis: z.string().max(500).optional(),
  /** Cancellation / refund policy. */
  annulation: z.string().max(800).optional(),
});
export type PracticalInfo = z.infer<typeof practicalInfoSchema>;

/** Shape used when creating/editing a formation in the admin (no server-managed fields). */
export const formationInputSchema = z.object({
  slug: slugSchema,
  title: z.string().min(2),
  shortDescription: z.string().max(280),
  description: z.string(),
  domain: formationDomainSchema,
  level: formationLevelSchema,
  durationHours: z.number().int().positive(),
  /** Total price of the formation. Shown ONLY in the inscription flow. */
  price: z.number().nonnegative(),
  /**
   * Per-module price (MAD). Shown everywhere on the public site EXCEPT the
   * inscription form. Default 0 — the public side falls back to `price / N`
   * if this is unset/zero.
   */
  prixParModule: z.number().nonnegative().default(0),
  currency: currencySchema.default('MAD'),
  formateurIds: z.array(z.string()).default([]),
  sessions: z.array(sessionSchema).default([]),
  programme: z.array(programmeModuleSchema).default([]),
  tags: z.array(z.string()).default([]),
  heroImage: z.string().url().nullable().default(null),
  programmePdf: z.string().url().nullable().default(null),
  status: formationStatusSchema,
  featured: z.boolean().default(false),
  /** Accent colour matching the formation poster. Default green. */
  colorTheme: formationColorThemeSchema.default('vert'),
  displayOrder: z.number().int().default(0),
  /** Optional per-formation overrides for the "Infos pratiques" tab. */
  practicalInfo: practicalInfoSchema.optional(),
  /** Optional certificate template — when set, admin can generate certificates
   *  for inscriptions instead of uploading them manually. */
  certificateTemplate: certificateTemplateSchema.optional(),
});
export type FormationInput = z.infer<typeof formationInputSchema>;

export const formationSchema = formationInputSchema.extend({
  id: z.string(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type Formation = z.infer<typeof formationSchema>;
